import React, { useEffect, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import DataTable from "react-data-table-component";
import * as Api from "../../../General/api";
import * as Common from "../../../General/common";
import ModalLoading from "../../workqueue/Modal/ModalLoading";
import PopupModal from "../../modal/popupmodal";
import ProviderDashboard from "../providers/providerDashboard";

/**
 * Associated Providers Tab — per spec Section 2.3.
 * Shows all active providers that have a CMN associated with the selected facility.
 * Provider names are hyperlinked — opens Provider Dashboard in a PopupModal.
 *
 * NOTE: Reuses CMN/Provider/List with an explicit facilityId query filter.
 * If a dedicated "associated providers for facility" endpoint is added, swap the API call here.
 * Q for PO: What additional columns (NPI, Pending CMN count) should appear beyond Provider Name?
 */
function AssociatedProvidersTab({ facilityId, facilityName }) {
  const [providers, setProviders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);

  var providerDashboardModal = null;

  useEffect(() => {
    if (!facilityId) return;
    fetchAssociatedProviders();
  }, [facilityId]);

  const fetchAssociatedProviders = () => {
    setIsLoading(true);
    Api.getRequestQry(
      Api.Uri_CmnProviderList,
      `?search=&sortColumn=ProviderName&sortDirection=asc&facilityId=${encodeURIComponent(
        String(facilityId || 0)
      )}&pageIndex=1&pageSize=200`
    )
      .then((res) => {
        if (res.data.statusCode === 200 && res.data.result) {
          setProviders(res.data.result.items || []);
        } else {
          setProviders([]);
        }
      })
      .catch((ex) => Common.LogError(ex, "AssociatedProvidersTab", "fetchAssociatedProviders"))
      .finally(() => setIsLoading(false));
  };

  const openProviderDashboard = (provider) => {
    setSelectedProvider(provider);
    setTimeout(() => {
      if (providerDashboardModal) providerDashboardModal.showModal();
    }, 100);
  };

  const columns = [
    {
      name: "Provider Name",
      sortable: true,
      minWidth: "200px",
      selector: (row) => row.providerName,
      cell: (row) => (
        <button
          type="button"
          className="btn btn-link p-0 text-left"
          onClick={() => openProviderDashboard(row)}
        >
          {row.providerName}
        </button>
      ),
    },
    {
      name: "NPI Number",
      sortable: true,
      minWidth: "130px",
      selector: (row) => Common.replaceNullOrEmptyWithString(row.npiNumber, "--"),
    },
    {
      name: "Phone",
      sortable: false,
      minWidth: "130px",
      selector: (row) => Common.replaceNullOrEmptyWithString(row.phone, "--"),
    },
    {
      name: "Pending CMN",
      sortable: true,
      minWidth: "130px",
      selector: (row) => row.pendingCmnCount,
      cell: (row) => (row.pendingCmnCount != null ? row.pendingCmnCount : 0),
    },
  ];

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <>
      <div className="react-dataTable">
        <DataTable
          noHeader
          striped
          highlightOnHover
          responsive
          columns={columns}
          data={providers}
          progressPending={isLoading}
          progressComponent={<ModalLoading />}
          className="react-dataTable"
          noDataComponent={
            <div className="text-center text-muted py-4">
              No associated providers found for this facility.
            </div>
          }
        />
      </div>

      {/* Provider Dashboard PopupModal */}
      <PopupModal
        size="xl"
        dialogClassName="modal-90w"
        title={
          selectedProvider
            ? `Provider: ${selectedProvider.providerName}`
            : "Provider Dashboard"
        }
        showButton={false}
        ref={(ref) => {
          providerDashboardModal = ref;
        }}
      >
        {selectedProvider && (
          <ProviderDashboard
            physicianId={selectedProvider.physicianId}
            providerName={selectedProvider.providerName}
          />
        )}
      </PopupModal>
    </>
  );
}

export default AssociatedProvidersTab;
